<template>
  <div
    v-if="showCampaignOffer"
    class="referral-banner"
    :class="{ 'referral-banner--wide': wide }"
  >
    <component
      :is="isAuthenticated ? 'a' : 'button'"
      v-bind="linkAttrs"
      class="referral-banner__link"
      :data-placement="placement"
      :aria-label="copy.ariaLabel"
      @click="onClick"
    >
      <span class="referral-banner__inner">
        <img
          class="referral-banner__owl"
          src="/static/img/yagiprime-referral/owl-letter.png"
          alt=""
          width="400"
          height="299"
        />
        <span class="referral-banner__body">
          <span class="referral-banner__badge">
            <span
              v-for="line in copy.badgeLines"
              :key="line"
              class="referral-banner__badge-line"
              >{{ line }}</span
            >
          </span>
          <span class="referral-banner__title">
            <span
              v-for="line in copy.titleLines"
              :key="line"
              class="referral-banner__title-line"
              >{{ line }}</span
            >
          </span>
        </span>
        <span class="referral-banner__circle" aria-hidden="true">
          <span class="referral-banner__circle-lead">{{
            copy.circleLead
          }}</span>
          <span class="referral-banner__circle-main">{{
            copy.circleMain
          }}</span>
        </span>
        <svg
          class="referral-banner__chevron"
          viewBox="0 0 12 20"
          aria-hidden="true"
        >
          <path d="M2 2l8 8-8 8" />
        </svg>
      </span>
    </component>
  </div>
</template>

<script setup lang="ts">
/**
 * ヤギプライム紹介割引キャンペーンの告知バナー。
 *
 * ヤギプライムへ遷移するボタン（志望動機・証明写真・確認画面の
 * ポップアップ）の近くに置き、「紹介すればお互い30日無料」を知らせる。
 * 押すとマイページ＞ヤギプライムのシェア枠へ行く。未ログインはマイページに
 * 入れない（requiresAuth で /login へ飛ばされ、編集中の画面から離れる）ので
 * ログインダイアログを開く。ログインダイアログを置いていない画面（扉ページ）は
 * loginPage を付け、ログインページ経由でシェア枠へ戻す（トップの帯と同じ）。
 * 出し分けはシェア枠と同じ条件（useReferralOfferVisibility）。
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router/composables'
import { emitOpenLoginDialog } from '@/plugins/emitter'
import { useReferralOfferVisibility } from '@/composables/yagiprime/useReferralOfferVisibility'
import campaign from '@/utils/yagiprime/referralCampaign.json'

const props = withDefaults(
  defineProps<{
    /** 計測（GTM の link_click）でどこのバナーかを区別する識別子 */
    placement: string
    /** 履歴書の編集中など、今の画面を離れさせたくない置き場所では別タブで開く */
    newTab?: boolean
    /** 未ログイン時、ログインダイアログではなくログインページへ送る（ダイアログの無い画面用） */
    loginPage?: boolean
    /** 全幅で置く場所（扉ページ）用。バッジと見出しを中央に揃え、幅があれば1行にする */
    wide?: boolean
  }>(),
  { newTab: false, loginPage: false, wide: false }
)

const copy = campaign.banner

const { isAuthenticated, showCampaignOffer } = useReferralOfferVisibility()
const router = useRouter()

const linkAttrs = computed(() => {
  if (!isAuthenticated.value) return { type: 'button' }
  return props.newTab
    ? { href: copy.path, target: '_blank', rel: 'noopener' }
    : { href: copy.path }
})

function onClick(): void {
  if (isAuthenticated.value) return
  if (props.loginPage) {
    // ログイン後にシェア枠へ戻す（NewLogin.vue の frompage 分岐）
    router.push({ path: '/login', query: { frompage: 'yagiprime_referral' } })
    return
  }
  emitOpenLoginDialog()
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/yagiprime/referral-campaign-banner';
</style>
